import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Store } from '@ngxs/store';
import {
  OperationObserverService,
  OperationSuccessState,
  isSuccessState,
} from '@x/common/operation';
import { CreateUserPaymentDialogComponent } from '@x/ecommerce-shop/app/core/auth/components/create-user-payment-dialog/create-user-payment-dialog.component';
import { AuthService } from '@x/ecommerce-shop/app/core/auth/services/auth.service';
import { AuthRefreshUserPaymentMethods } from '@x/ecommerce-shop/app/core/auth/state/auth.actions';
import {
  IShopPaymentMethod,
  IShopUserPaymentMethod,
  ShopUserService,
} from '@x/ecommerce/shop-client';
import { Observable, of, timer } from 'rxjs';
import { filter, map, share, switchMap } from 'rxjs/operators';

export interface CreateUserPaymentDialogData {
  iframeUrl: SafeResourceUrl;
  userPaymentMethod: IShopUserPaymentMethod;
}

export type CreateUserPaymentDialogResponse = IShopUserPaymentMethod | null | undefined;
export interface ICreateUserPaymentDialogResponse {
  success: boolean;
  userPaymentMethod: IShopUserPaymentMethod;
}

@Injectable()
export class UserPaymentService {
  constructor(
    private shopUserService: ShopUserService,
    private dialog: MatDialog,
    private operationObserverService: OperationObserverService,
    private sanitizer: DomSanitizer,
    private store: Store,
    private authService: AuthService,
  ) {}

  cancelUserPaymentMethod$(userPaymentMethodId: number): Observable<IShopUserPaymentMethod> {
    return this.shopUserService.cancelUserPaymentMethod({
      input: { userPaymentMethodId },
    });
  }

  createUserPaymentMethod$(
    paymentMethod: IShopPaymentMethod,
  ): Observable<CreateUserPaymentDialogResponse> {
    const userId = this.authService.userId;
    if (!userId) return of(null);

    const createUserPayment$ = this.shopUserService.createUserPaymentMethod({
      input: { paymentMethodId: paymentMethod.id },
    });

    return createUserPayment$.pipe(
      switchMap((userPaymentMethod) => {
        const { registrationFormUrl } = userPaymentMethod.publicAttributes;

        return this.openDialog(
          {
            iframeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(registrationFormUrl),
            userPaymentMethod: userPaymentMethod,
          },
          {
            paymentMethod: paymentMethod,
            allowFullScreen: true,
          },
        ).pipe(
          switchMap((dialogResponse) => {
            return this.store
              .dispatch(new AuthRefreshUserPaymentMethods())
              .pipe(map(() => dialogResponse));
          }),
        );
      }),
    );
  }

  startPollingPaymentState$(userPaymentMethodId: number): Observable<IShopUserPaymentMethod> {
    return timer(0, 3000).pipe(
      switchMap(() =>
        this.operationObserverService.finalize(
          this.shopUserService.fetchUserPaymentMethod({ id: userPaymentMethodId }),
        ),
      ),
      filter((o: any): o is OperationSuccessState<IShopUserPaymentMethod> => isSuccessState(o)),
      map((o) => o.data),
      share(),
    );
  }

  private openDialog(
    data: CreateUserPaymentDialogData,
    {
      paymentMethod,
      allowFullScreen = false,
      panelClass = 'fullscreen-dialog-mobile',
    }: {
      paymentMethod: IShopPaymentMethod;
      allowFullScreen?: boolean;
      panelClass?: string;
    },
  ): Observable<CreateUserPaymentDialogResponse> {
    const isFullScreen = allowFullScreen && paymentMethod.fullScreenPaymentEnabled;
    const classNameOverride = isFullScreen ? 'fullscreen-dialog' : panelClass;
    return this.dialog
      .open<
        CreateUserPaymentDialogComponent,
        CreateUserPaymentDialogData,
        CreateUserPaymentDialogResponse
      >(CreateUserPaymentDialogComponent, {
        data,
        width: '500px',
        panelClass: classNameOverride,
        disableClose: true,
      })
      .afterClosed();
  }
}
